import React from 'react';

/* Bespoke two-tone property icons (currentColor + #E87A20 accent) for the
   product pages' material-property grids — matching the MedicalIcons language. */

const ACCENT = '#E87A20';
const base = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};
const I = ({ children, className, ...p }) => (
  <svg {...base} className={className} {...p}>{children}</svg>
);

/** Flexible / bendable after soaking */
export const MoldableIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M4 8a4 4 0 0 1 4-4h2.5" />
    <path d="M20 16a4 4 0 0 1-4 4h-2.5" />
    <path d="M4 8v8M20 16V8" />
    <path d="M8.5 12h7" stroke={accent} />
    <polyline points="13 9.5 15.5 12 13 14.5" stroke={accent} />
  </I>
);

/** Bio-compatible — integration with bone tissue (interlocking) */
export const BiocompatibleIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <circle cx="9" cy="12" r="4.2" />
    <circle cx="15" cy="12" r="4.2" stroke={accent} />
  </I>
);

/** Mechanical parameters — structural support (load on column) */
export const MechanicalIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <rect x="9" y="7" width="6" height="11" rx="1" />
    <path d="M6 7h12M6 18h12" />
    <line x1="12" y1="2.5" x2="12" y2="5.2" stroke={accent} />
    <polyline points="10 4 12 2 14 4" stroke={accent} />
  </I>
);

/** Supports bone reconstruction — scaffold of blocks */
export const ReconstructionIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <rect x="3.5" y="13" width="6" height="6" rx="0.9" />
    <rect x="14.5" y="13" width="6" height="6" rx="0.9" />
    <rect x="9" y="4.8" width="6" height="6" rx="0.9" stroke={accent} />
    <line x1="12" y1="10.8" x2="12" y2="13" stroke={accent} />
  </I>
);

/** Absorbs blood & medicine — droplet into basin */
export const AbsorptionIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M5 14a7 7 0 0 0 14 0" />
    <path d="M12 3c2 2.6 3.2 4.3 3.2 5.9a3.2 3.2 0 0 1-6.4 0C8.8 7.3 10 5.6 12 3z" stroke={accent} />
  </I>
);

/** Bone tissue regeneration — renewal cycle + new growth */
export const RegenerationIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M20 11a8 8 0 1 0-1.7 5.6" />
    <polyline points="20 4.5 20 11 13.6 11" />
    <path d="M12 9v6M9 12h6" stroke={accent} />
  </I>
);

/** Ionic reactive / bioactive — crystal lattice + ion spark */
export const BioactiveIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M12 3.5l7 4v9l-7 4-7-4v-9l7-4z" />
    <polyline points="12.6 8 10.4 12 13 12 11 16.2" stroke={accent} />
  </I>
);

/** Non-toxic — safety shield + check */
export const NonToxicIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M12 3l7 3v5.4c0 4.4-3 8.2-7 9.4-4-1.2-7-5-7-9.4V6l7-3z" />
    <polyline points="9 12 11.2 14.2 15.4 9.9" stroke={accent} strokeWidth="1.9" />
  </I>
);

/** Defect span — measurement ruler with span arrows */
export const DefectSpanIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <rect x="3" y="8.5" width="18" height="6" rx="1.2" />
    <line x1="7.5" y1="8.5" x2="7.5" y2="11.5" />
    <line x1="12" y1="8.5" x2="12" y2="11.5" />
    <line x1="16.5" y1="8.5" x2="16.5" y2="11.5" />
    <path d="M4 18.5h16" stroke={accent} />
    <polyline points="6 17 4 18.5 6 20" stroke={accent} />
    <polyline points="18 17 20 18.5 18 20" stroke={accent} />
  </I>
);

/** Non-animal / synthetic — molecule */
export const NonAnimalIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <line x1="11" y1="7.6" x2="6.7" y2="14" />
    <line x1="13" y1="7.6" x2="17.3" y2="14" />
    <circle cx="6" cy="16" r="2.2" />
    <circle cx="18" cy="16" r="2.2" />
    <circle cx="12" cy="6" r="2.4" stroke={accent} />
  </I>
);

/** Easy to store & transport — 3D box */
export const StorageIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M4 8l8-4 8 4v8l-8 4-8-4V8z" />
    <path d="M4 8l8 4 8-4M12 12v8" />
    <line x1="8" y1="6" x2="16" y2="10" stroke={accent} />
  </I>
);

/** Antibiotic carrier — flask soaked with drug */
export const AntibioticCarrierIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M10 3h4M11 3v4L7.6 15.5A3 3 0 0 0 10.4 20h3.2a3 3 0 0 0 2.8-4.5L13 7V3" />
    <line x1="9" y1="13" x2="15" y2="13" stroke={accent} />
    <circle cx="11" cy="16" r="0.7" fill={accent} stroke={accent} />
    <circle cx="13.4" cy="17" r="0.7" fill={accent} stroke={accent} />
  </I>
);

/** Bone healing 50% faster — pulse + speed bolt */
export const FastHealingIcon = ({ accent = ACCENT, ...p }) => (
  <I {...p}>
    <path d="M2 14h4l2.4-6 3.4 11 2.4-5H22" />
    <polyline points="18 3 16 7 18.4 7 16 11" stroke={accent} />
  </I>
);
